import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { Truck, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Vehicles() {
  const { meta, user } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [sites, setSites] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [typeFilter, setTypeFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");

  useEffect(() => { api.get("/sites").then(({ data }) => setSites(data)); }, []);
  const load = () => {
    const p = new URLSearchParams();
    if (typeFilter !== "all") p.set("vehicle_type", typeFilter);
    if (classFilter !== "all") p.set("vehicle_class", classFilter);
    if (siteFilter !== "all") p.set("mine_site_id", siteFilter);
    api.get(`/vehicles?${p.toString()}`).then(({ data }) => setVehicles(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter, classFilter, siteFilter]);

  const isAdmin = user?.role === "admin";
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => s.size === vehicles.length ? new Set() : new Set(vehicles.map((v) => v.id)));
  const delOne = async (id, num) => {
    await api.delete(`/vehicles/${id}`);
    setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
    load();
    toast.success(`Dumper ${num} deleted`, { duration: 8000,
      action: { label: "Undo", onClick: async () => { await api.post(`/vehicles/${id}/restore`); load(); toast.success(`Dumper ${num} restored`); } } });
  };
  const delBulk = async () => {
    const ids = [...selected];
    if (!window.confirm(`Delete ${ids.length} selected dumper(s)?`)) return;
    await api.post("/vehicles/bulk-delete", { ids });
    setSelected(new Set()); load();
    toast.success(`${ids.length} dumper(s) deleted`, { duration: 8000,
      action: { label: "Undo", onClick: async () => { await api.post("/vehicles/bulk-restore", { ids }); load(); toast.success("Dumpers restored"); } } });
  };

  const cleanupNames = async () => {
    if (!window.confirm("Remove the mistaken 'sany' text from all dumper names?\n\n60T dumpers (16.00.25) will automatically keep a 'SANY' brand; others become just the number. This runs once for the current customer and is safe to repeat.")) return;
    try {
      const { data } = await api.post("/admin/normalize-fleet-names");
      toast.success(`${data.updated} of ${data.total} dumper name(s) cleaned up`);
      load();
    } catch (e) { toast.error(`Cleanup failed: ${e.response?.data?.detail || e.message}`); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Fleet</h1>
          <p className="mt-1 text-sm text-zinc-400">Dumpers &amp; trucks are auto-registered when tyres are fitted to them.</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={cleanupNames} data-testid="cleanup-names-btn"
              className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10">Clean up names</Button>
          )}
          {isAdmin && selected.size > 0 && (
            <Button variant="outline" onClick={delBulk} data-testid="bulk-delete-vehicles"
              className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"><Trash size={16} weight="bold" className="mr-1" /> Delete ({selected.size})</Button>
          )}
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger data-testid="veh-class-filter" className="w-36 bg-zinc-900"><SelectValue placeholder="Class" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vehicles</SelectItem>
              <SelectItem value="Dumper">Dumpers</SelectItem>
              <SelectItem value="Truck">Trucks</SelectItem>
            </SelectContent>
          </Select>
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger data-testid="veh-site-filter" className="w-44 bg-zinc-900"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Sites</SelectItem>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="veh-type-filter" className="w-44 bg-zinc-900"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem>
              {(meta?.vehicle_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-sm border border-zinc-800">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-zinc-900 text-left overline text-zinc-500">
            <tr>
              <th className="px-3 py-3 w-8">{isAdmin && <Checkbox checked={vehicles.length > 0 && selected.size === vehicles.length} onCheckedChange={toggleAll} data-testid="select-all-vehicles" />}</th>
              <th className="px-4 py-3">Dumper #</th><th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Tyre Size</th><th className="px-4 py-3">Site</th>
              <th className="px-4 py-3">Engine Hours</th><th className="px-4 py-3">Fitted</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {vehicles.map((v) => (
              <tr key={v.id} data-testid={`vehicle-row-${v.id}`} onClick={() => navigate(`/vehicles/${v.id}`)} className="group cursor-pointer bg-zinc-950 hover:bg-zinc-900">
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>{isAdmin && <Checkbox checked={selected.has(v.id)} onCheckedChange={() => toggle(v.id)} data-testid={`select-vehicle-${v.id}`} />}</td>
                <td className="px-4 py-3 font-mono font-bold text-amber-500">{v.fleet_number}</td>
                <td className="px-4 py-3"><span className="flex items-center gap-1"><Truck size={14} />{v.vehicle_type}</span></td>
                <td className="px-4 py-3 font-mono text-zinc-400">{v.tyre_size}</td>
                <td className="px-4 py-3 text-zinc-400">{v.mine_site_name}</td>
                <td className="px-4 py-3 tabular-nums">{Math.round(v.current_hours || 0).toLocaleString()} h</td>
                <td className="px-4 py-3 tabular-nums">{v.fitted_tyres} / {(v.positions_layout || []).length || 10}</td>
                <td className="px-4 py-3"><StatusBadge status={v.status} /></td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  {isAdmin && <button onClick={() => delOne(v.id, v.fleet_number)} data-testid={`delete-vehicle-${v.id}`} className="text-zinc-600 transition-colors hover:text-rose-500"><Trash size={15} /></button>}
                </td>
              </tr>
            ))}
            {vehicles.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-500">No dumpers yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
